import React from 'react';
import Combobox, { ComboboxProps } from '~/app/shared/components/ui/primitives/Combobox';
import { Field } from '~/app/shared/components/Form';
import { FieldProps } from '~/app/shared/components/Form/Field';
import { useOneDimension } from '~/app/shared/dimensions/hooks/useDimensions';
import { useDimensionComboboxData } from './DimensionLineCombobox.utils';
import DimensionLinesListView from './DimensionLinesListView';
import { validateDimensionValueId } from './DimensionLineCombobox.validators';
import { DimensionLine } from '../types';

interface DimensionLineComboboxBaseProps {
    dimensionId: number;
    initialData?: DimensionLine;
    hideSummary?: boolean;
    hideInlineSearch?: boolean;
    enableCreate?: boolean;
    onCreated?: () => void;
    includeDistributionKeys?: boolean;
    includeBarred?: boolean;
}

const DimensionLineComboboxBase: React.FC<DimensionLineComboboxBaseProps & ComboboxProps> = ({
    dimensionId,
    initialData,
    hideSummary = false,
    hideInlineSearch = false,
    enableCreate = true,
    onCreated,
    includeDistributionKeys = true,
    includeBarred = false,
    modalSize = 'sm',
    style = { display: 'flex' },
    ...props
}) => {
    const [dimensionQuery, data] = useDimensionComboboxData({
        dimensionId,
        includeDistributionKeys,
        includeBarredLines: includeBarred,
        hideInlineSearch,
        hideSummary,
    });

    return (
        <Combobox
            {...props}
            aria-label={`Dimension${dimensionId}`}
            data={data}
            modal={({ hide, onChange }) => (
                <DimensionLinesListView
                    dimensionId={dimensionId}
                    dimensionLines={dimensionQuery.data?.lines}
                    enableCreate={enableCreate}
                    onCreated={(dimensionLine: DimensionLine) => {
                        onCreated?.();
                        onChange(dimensionLine.key, dimensionLine);

                        hide();
                    }}
                    onSelected={(dimensionLine: DimensionLine) => {
                        onChange(dimensionLine.key, dimensionLine);
                        hide();
                    }}
                    includeDistributionKeys={includeDistributionKeys}
                />
            )}
            modalSize={modalSize}
            style={style}
        />
    );
};

type DimensionLineComboboxProps = DimensionLineComboboxBaseProps &
    FieldProps<unknown> & { as?: never; children?: never; useInternalValidation?: boolean };
const DimensionLineCombobox = ({
    data,
    modal,
    includeDistributionKeys = true,
    includeBarred = false,
    useInternalValidation = true,
    ...props
}: DimensionLineComboboxProps) => {
    const dimensionQuery = useOneDimension(props.dimensionId, {
        includeDistributionKeys,
        includeBarredLines: includeBarred,
        skip: !useInternalValidation,
    });
    if (useInternalValidation) {
        props.validate = (value?: number) => validateDimensionValueId(value, dimensionQuery.data?.lines);
    }

    return (
        <Field.Combobox
            truncateLabel
            {...props}
            as={DimensionLineComboboxBase}
            includeDistributionKeys={includeDistributionKeys}
            includeBarred={includeBarred}
        />
    );
};

export default DimensionLineCombobox;
